import {
  type UseMutateAsyncFunction,
  useMutation,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { z } from 'zod';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import {
  EmailSchema,
  toSnakeCase,
  withoutObjectKeysWhereValueIs,
} from '@vestigas/utils';

const zodCreateUserSchema = z
  .object({
    companyId: z.uuid(),
    email: EmailSchema,
    firstName: z.string().trim(),
    isActive: z.boolean(),
    isSynced: z.boolean().optional(),
    lastName: z.string().trim(),
    notificationSettings: z
      .object({
        delayDuration: z.coerce.number().nonnegative().optional(),
        sendEmail: z.boolean().optional(),
      })
      .optional(),
    orgUnits: z.array(z.uuid()).optional(),
    password: z.string().min(8),
    permittedSignatureTypes: z.array(
      z.string().transform((value) => toSnakeCase(value)),
    ),
    position: z.union([z.literal(''), z.string().trim()]).optional(),
    username: z.union([z.literal(''), z.string().trim()]).optional(),
    userType: z.union([z.literal(''), z.string()]).nullish(),
  })
  .transform((data) => withoutObjectKeysWhereValueIs(data, [undefined]));

type CreateUserPayload = z.infer<typeof zodCreateUserSchema>;

type CreateUserResponse = {
  code: number;
  id: UUID;
  message: string;
};

type CreateUserMutationOptions = UseMutationOptions<
  CreateUserResponse,
  Error,
  CreateUserPayload
>;

export type CreateUserMutateAsyncFunction = UseMutateAsyncFunction<
  CreateUserResponse,
  Error,
  CreateUserPayload
>;

/**
 * Creates a new user as ADMIN.
 *
 * @param {Object} userData - The user data for creating a new user.
 * @returns {Promise} A promise that resolves with the API response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Admin/operation/create_user_admin_users_post
 */
export const createUserAdmin = async (userData: CreateUserPayload) => {
  const response = await vestigasApi
    .post(ENDPOINT.USER.CREATE_ADMIN(), {
      json: userData,
      validate: {
        body: zodCreateUserSchema,
      },
    })
    .json<CreateUserResponse>();

  return response;
};

/**
 * Custom hook for creating a new user as ADMIN.
 *
 * @returns {Object} An object containing the mutation function and related properties.
 */
export const useMutationCreateUserAdmin = (
  options?: CreateUserMutationOptions,
) => {
  return useMutation({
    mutationFn: async (userData: CreateUserPayload) =>
      createUserAdmin(userData),
    ...options,
  });
};
